import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, Check, X, ArrowRight, Flame } from "lucide-react";
import api from "../../lib/api";
import { Reveal } from "./Reveal";

const LETTERS = ["A", "B", "C", "D", "E"];
const STREAK_KEY = "asa_quiz_streak";

const dateKey = (d = new Date()) => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;

const loadStreak = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STREAK_KEY) || "{}");
    return { lastDate: raw.lastDate || null, current: raw.current || 0, best: raw.best || 0 };
  } catch {
    return { lastDate: null, current: 0, best: 0 };
  }
};

// Records today's play once per day and returns the updated streak.
const recordStreak = () => {
  const s = loadStreak();
  const today = dateKey();
  if (s.lastDate === today) return s; // already counted today
  const y = new Date();
  y.setDate(y.getDate() - 1);
  const yesterday = dateKey(y);
  const current = s.lastDate === yesterday ? s.current + 1 : 1;
  const next = { lastDate: today, current, best: Math.max(s.best, current) };
  try {
    localStorage.setItem(STREAK_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
};

export const QuizOfDay = () => {
  const [q, setQ] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  const [answered, setAnswered] = React.useState(false);
  const [streak, setStreak] = React.useState({ lastDate: null, current: 0, best: 0 });
  const [playedToday, setPlayedToday] = React.useState(false);

  React.useEffect(() => {
    api.get("/quiz/question-of-the-day").then((r) => setQ(r.data)).catch(() => {});
    const s = loadStreak();
    setStreak(s);
    setPlayedToday(s.lastDate === dateKey());
  }, []);

  if (!q) return null;

  const choose = (i) => {
    if (answered) return;
    setSelected(i);
    setAnswered(true);
    setStreak(recordStreak());
    setPlayedToday(true);
  };

  const isCorrectPick = answered && selected === q.correct_index;

  return (
    <section className="bg-sand border-y border-parchment" data-testid="quiz-of-day-section">
      <div className="max-w-4xl mx-auto px-6 py-24">
        <Reveal>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-forest" />
              <p className="uppercase tracking-[0.25em] text-xs text-gold">Quiz of the Day · Ìdánwò</p>
            </div>
            {streak.current > 0 && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-forest/10 text-forest text-xs font-semibold px-3 py-1"
                data-testid="quiz-of-day-streak"
                title={`Best streak: ${streak.best} ${streak.best === 1 ? "day" : "days"}`}
              >
                <Flame className="w-3.5 h-3.5" />
                {streak.current}-day streak
              </span>
            )}
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-ink leading-tight mb-10">
            {playedToday ? "You've answered today's question" : "Can you answer today's question?"}
          </h2>
        </Reveal>

        <Reveal delay={0.08}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl border border-parchment bg-cream p-8 md:p-10"
            data-testid="quiz-of-day-card"
          >
            <p className="text-[11px] uppercase tracking-[0.2em] text-gold mb-3">{q.category}</p>
            {q.image && (
              <img src={q.image} alt="" loading="lazy" className="w-full max-h-64 object-cover rounded-xl mb-6" data-testid="quiz-of-day-image" />
            )}
            <h3 className="font-serif text-2xl md:text-3xl text-ink leading-snug mb-7" data-testid="quiz-of-day-question">
              {q.question}
            </h3>

            <div className="grid gap-3 sm:grid-cols-2">
              {q.options.map((opt, i) => {
                const isCorrect = i === q.correct_index;
                const isChosen = i === selected;
                let cls = "border-parchment hover:border-forest hover:bg-forest/5 text-ink";
                if (answered) {
                  if (isCorrect) cls = "border-forest bg-forest/10 text-ink";
                  else if (isChosen) cls = "border-red-400 bg-red-50 text-ink";
                  else cls = "border-parchment opacity-55 text-ink";
                }
                return (
                  <button
                    key={i}
                    onClick={() => choose(i)}
                    disabled={answered}
                    data-testid={`quiz-of-day-option-${i}`}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 text-left transition-all disabled:cursor-default ${cls}`}
                  >
                    <span className="grid place-items-center w-7 h-7 rounded-full border border-current text-xs font-semibold shrink-0 text-forest">
                      {LETTERS[i]}
                    </span>
                    <span className="flex-1">{opt}</span>
                    {answered && isCorrect && <Check className="w-4 h-4 text-forest shrink-0" />}
                    {answered && isChosen && !isCorrect && <X className="w-4 h-4 text-red-500 shrink-0" />}
                  </button>
                );
              })}
            </div>

            {answered && (
              <div className="mt-6 rounded-xl bg-sand border border-parchment p-5" data-testid="quiz-of-day-feedback">
                <p className={`font-semibold mb-1 ${isCorrectPick ? "text-forest" : "text-red-500"}`}>
                  {isCorrectPick ? "Ẹ kú iṣẹ́! Correct." : "Not quite."}
                </p>
                {q.explanation && <p className="text-ink-muted leading-relaxed">{q.explanation}</p>}
                <p className="mt-3 flex items-center gap-1.5 text-sm font-medium text-forest" data-testid="quiz-of-day-streak-note">
                  <Flame className="w-4 h-4" />
                  {streak.current === 1
                    ? "Streak started! Come back tomorrow to keep it going."
                    : `You're on a ${streak.current}-day streak — see you tomorrow!`}
                </p>
              </div>
            )}

            <Link
              to="/quiz"
              data-testid="quiz-of-day-link"
              className="mt-8 inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-6 py-3 rounded-full hover:bg-forest-light transition-colors"
            >
              {answered ? "Take the full quiz" : "Play the full quiz"}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </Reveal>
      </div>
    </section>
  );
};

export default QuizOfDay;
